import { useEffect, useMemo, useState } from 'react';
import { useAtomValue } from 'jotai';
import { FileSpreadsheet, Search } from 'lucide-react';
import { toast } from '@/components/ui/sonner';
import { Button } from '@/components/ui/button';
import { DateRangePicker } from '@/components/ui/date-range-picker';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle
} from '@/components/ui/sheet';
import { Spinner } from '@/components/ui/spinner';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Textarea } from '@/components/ui/textarea';
import { selectedAccountIdAtom } from '@/hooks/use-account';
import { useBusinessUnits } from '@/hooks/use-business-units';
import { refreshPulseImports } from '@/hooks/use-pulse-imports';
import { pulseImportService } from '@/services/pulse-onboarding.service';
import { cn } from '@/lib/utils';
import type { PulseImportCheck, PulseImportIntent, PulseImportMode, PulseImportRequest } from '@/types';
import { PulseOnboardingButton } from './pulse-onboarding-button';
import {
  buildFiltersRequest,
  buildIdsRequest,
  DEFAULT_MAX_TICKETS,
  guessIdColumn,
  PULSE_IMPORT_MAX_TICKETS,
  readTicketIdSheet,
  ticketIdsFromColumn,
  ticketIdsFromText,
  type BuiltRequest,
  type TicketIdSheet
} from '../pulse-import';

const errorMessage = (error: unknown, fallback: string) =>
  error instanceof Error && error.message ? error.message : fallback;

const Section = ({ title, children }: { title: string; children: React.ReactNode }) => (
  <section className='space-y-3 px-5 py-4'>
    <h4 className='text-sm font-medium'>{title}</h4>
    {children}
  </section>
);

function Metric({ label, value }: { label: string; value: string }) {
  return (
    <div className='rounded-md border bg-background p-3'>
      <p className='text-xs text-muted-foreground'>{label}</p>
      <p className='text-xl font-medium tabular-nums'>{value}</p>
    </div>
  );
}

const count = (value: number | null) => (value == null ? '–' : value.toLocaleString());

/** What a check found, in the words the operator decides on. */
function CheckSummary({ check }: { check: PulseImportCheck }) {
  if (!check.exact) {
    return (
      <p className='text-sm text-muted-foreground'>
        Imports up to {check.to_import.toLocaleString()} tickets closed in the range, newest first.
        The helpdesk is walked while it runs, so there is no count ahead of time.
      </p>
    );
  }
  return (
    <div className='space-y-2'>
      <div className='grid grid-cols-3 gap-2'>
        <Metric label='Matched' value={count(check.match_count)} />
        <Metric label='Already in Pulse' value={count(check.already_in_pulse)} />
        <Metric label='Will import' value={check.to_import.toLocaleString()} />
      </div>
      {check.to_import === 0 && (
        <p className='text-xs text-muted-foreground'>Nothing new to import.</p>
      )}
    </div>
  );
}

interface IntentPickerProps {
  intents: PulseImportIntent[];
  selected: string[];
  loading: boolean;
  error: string | null;
  onToggle: (id: string) => void;
}

/** The brand's intents. The import classifies each ticket it walks against them. */
export function IntentPicker({ intents, selected, loading, error, onToggle }: IntentPickerProps) {
  if (loading) {
    return (
      <p className='flex items-center gap-2 text-xs text-muted-foreground'>
        <Spinner className='size-3.5' /> Loading intents…
      </p>
    );
  }
  if (error) return <p className='text-xs text-red-foreground'>{error}</p>;
  if (intents.length === 0) {
    return (
      <p className='text-xs text-muted-foreground'>
        This brand has no intents set up. Leave this empty to import by date.
      </p>
    );
  }
  return (
    <div className='flex max-h-40 flex-wrap gap-1.5 overflow-y-auto rounded-md border bg-background p-2'>
      {intents.map((intent) => {
        const on = selected.includes(intent.id);
        return (
          <button
            key={intent.id}
            type='button'
            aria-pressed={on}
            title={intent.category || undefined}
            onClick={() => onToggle(intent.id)}
            className={cn(
              'rounded-full border px-2.5 py-0.5 text-xs transition-colors',
              on ? 'border-primary bg-primary text-primary-foreground' : 'hover:bg-muted'
            )}
          >
            {intent.name}
          </button>
        );
      })}
    </div>
  );
}

function MaxTickets({ value, onChange }: { value: number; onChange: (value: number) => void }) {
  return (
    <div className='flex items-center gap-3'>
      <Label htmlFor='pulse-import-max' className='min-w-28 font-normal text-muted-foreground'>
        Max tickets
      </Label>
      <Input
        id='pulse-import-max'
        type='number'
        min={1}
        max={PULSE_IMPORT_MAX_TICKETS}
        value={Number.isFinite(value) ? value : ''}
        onChange={(event) => onChange(Number(event.target.value))}
        className='w-32'
      />
    </div>
  );
}

/** The sheet's tabs: the account's full history (onboarding import), or chosen tickets. */
type ImportTab = 'full' | PulseImportMode;

/**
 * The onboarding import, moved here from the Pulse card so the card has one import entry
 * point. Same button and the same brand-mapping forecast as before.
 */
function FullHistoryTab() {
  return (
    <Section title='Full history'>
      <p className='text-sm text-muted-foreground'>
        Imports the account&apos;s latest tickets. How many is the merchant&apos;s own answer
        from the signup wizard, so there is nothing to enter here. Each ticket&apos;s brand comes
        from the inbox it arrived on.
      </p>
      <PulseOnboardingButton />
      <p className='text-xs text-muted-foreground'>
        Its progress shows in Pulse, not in the import list on the account page.
      </p>
    </Section>
  );
}

interface PulseImportSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/**
 * The Import tickets sheet: choose tickets by closed date (optionally only those of chosen
 * intents, which jobs classifies as it walks) or by uploading their IDs, check the counts,
 * then start. jobs runs the import; the account's Pulse card shows it until it finishes.
 */
export function PulseImportSheet({ open, onOpenChange }: PulseImportSheetProps) {
  const accountId = useAtomValue(selectedAccountIdAtom);
  const { data: businessUnits = [] } = useBusinessUnits();
  const [tab, setTab] = useState<ImportTab>('full');
  // The filters tab is also what an operator lands on for chosen tickets; full history has no request.
  const mode: PulseImportMode = tab === 'ids' ? 'ids' : 'filters';

  const defaultBuId = (businessUnits.find((unit) => unit.is_default) ?? businessUnits[0])?.id ?? null;
  const [buId, setBuId] = useState<number | null>(null);
  const brandId = buId ?? defaultBuId;
  const [range, setRange] = useState<{ startDate?: Date; endDate?: Date }>({});
  const [intents, setIntents] = useState<string[]>([]);
  const [filtersMax, setFiltersMax] = useState(DEFAULT_MAX_TICKETS);

  const [sheet, setSheet] = useState<TicketIdSheet | null>(null);
  const [fileName, setFileName] = useState<string | null>(null);
  const [column, setColumn] = useState<string | null>(null);
  const [pasted, setPasted] = useState('');
  const [idsMax, setIdsMax] = useState<number | null>(null);

  const [available, setAvailable] = useState<PulseImportIntent[]>([]);
  const [intentsLoading, setIntentsLoading] = useState(false);
  const [intentsError, setIntentsError] = useState<string | null>(null);
  const [check, setCheck] = useState<{ key: string; result: PulseImportCheck } | null>(null);
  const [busy, setBusy] = useState<'check' | 'start' | null>(null);

  const ticketIds = useMemo(
    () => (sheet && column ? ticketIdsFromColumn(sheet, column) : ticketIdsFromText(pasted)),
    [sheet, column, pasted]
  );
  const idsMaxCount = idsMax ?? Math.min(ticketIds.length || 1, PULSE_IMPORT_MAX_TICKETS);

  const built: BuiltRequest =
    mode === 'filters'
      ? buildFiltersRequest({ buId: brandId, ...range, intents, maxCount: filtersMax })
      : buildIdsRequest({ ticketIds, maxCount: idsMaxCount });
  const requestKey = 'request' in built ? JSON.stringify(built.request) : null;
  const currentCheck = check && check.key === requestKey ? check.result : null;

  // The intent picker lists the brand's intents. The check that returns them needs dates,
  // so it loads once dates are set and again only when the brand changes.
  const rangeKey = `${brandId}|${Boolean(range.startDate && range.endDate)}`;
  useEffect(() => {
    if (!open || tab !== 'filters' || accountId == null) return;
    const listing = buildFiltersRequest({ buId: brandId, ...range, intents: [], maxCount: filtersMax });
    if (!('request' in listing)) return;
    let cancelled = false;
    setIntentsLoading(true);
    setIntentsError(null);
    pulseImportService
      .check(accountId, listing.request)
      .then((result) => {
        if (cancelled) return;
        setAvailable(result.intents_available);
        setIntentsError(result.intents_error ?? null);
        setIntents((chosen) => chosen.filter((id) => result.intents_available.some((intent) => intent.id === id)));
      })
      .catch((error) => {
        if (cancelled) return;
        console.error('PulseImportSheet: loading intents failed', error);
        setIntentsError(errorMessage(error, "Couldn't load intents."));
      })
      .finally(() => !cancelled && setIntentsLoading(false));
    return () => {
      cancelled = true;
    };
    // Reload on brand only; dates, the max and the chosen intents don't change the list.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, tab, accountId, rangeKey]);

  const reset = () => {
    setCheck(null);
    setSheet(null);
    setFileName(null);
    setColumn(null);
    setPasted('');
    setIdsMax(null);
    setIntents([]);
  };

  const onFile = async (file: File | undefined) => {
    if (!file) return;
    try {
      const read = await readTicketIdSheet(file);
      if (read.rows.length === 0) {
        toast.error('That file has no rows.');
        return;
      }
      setSheet(read);
      setFileName(file.name);
      setColumn(guessIdColumn(read.columns));
      setIdsMax(null);
    } catch (error) {
      console.error('PulseImportSheet: reading the file failed', { fileName: file.name, error });
      toast.error("Couldn't read that file. Use a .csv or .xlsx file.");
    }
  };

  const runCheck = async () => {
    if (!('request' in built) || accountId == null || requestKey == null) return;
    setBusy('check');
    try {
      const result = await pulseImportService.check(accountId, built.request);
      setCheck({ key: requestKey, result });
    } catch (error) {
      console.error('PulseImportSheet: check failed', error);
      toast.error(errorMessage(error, "Couldn't check the tickets."));
    } finally {
      setBusy(null);
    }
  };

  const start = async () => {
    if (!('request' in built) || accountId == null) return;
    setBusy('start');
    try {
      const started = await pulseImportService.start(accountId, built.request as PulseImportRequest);
      toast.success(
        started.requested > 0
          ? `Import started for ${started.requested.toLocaleString()} tickets.`
          : `Import started, up to ${started.max_count.toLocaleString()} tickets.`
      );
      await refreshPulseImports(accountId);
      reset();
      onOpenChange(false);
    } catch (error) {
      console.error('PulseImportSheet: start failed', error);
      toast.error(errorMessage(error, "Couldn't start the import."));
    } finally {
      setBusy(null);
    }
  };

  const canStart = currentCheck != null && currentCheck.to_import > 0 && busy == null;

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className='flex w-full flex-col gap-0 bg-background p-0 backdrop-blur-none sm:max-w-lg'>
        <SheetHeader className='shrink-0 border-b px-5 py-4'>
          <SheetTitle>Import tickets to Pulse</SheetTitle>
          <SheetDescription>
            Each ticket gets an insight and a CSAT score. Imported tickets never get a draft.
          </SheetDescription>
        </SheetHeader>

        <div className='min-h-0 flex-1 divide-y overflow-y-auto'>
          <div className='px-5 py-3'>
            <Tabs value={tab} onValueChange={(value) => setTab(value as ImportTab)}>
              <TabsList>
                <TabsTrigger value='full'>Full history</TabsTrigger>
                <TabsTrigger value='filters'>By filters</TabsTrigger>
                <TabsTrigger value='ids'>By ticket IDs</TabsTrigger>
              </TabsList>
            </Tabs>
          </div>

          {tab === 'full' ? (
            <FullHistoryTab />
          ) : tab === 'filters' ? (
            <Section title='Which tickets'>
              <div className='flex items-center gap-3'>
                <Label className='min-w-28 font-normal text-muted-foreground'>Brand</Label>
                <Select
                  value={brandId != null ? String(brandId) : undefined}
                  onValueChange={(value) => setBuId(Number(value))}
                >
                  <SelectTrigger className='min-w-56' aria-label='Brand'>
                    <SelectValue placeholder='Choose a brand' />
                  </SelectTrigger>
                  <SelectContent>
                    {businessUnits.map((unit) => (
                      <SelectItem key={unit.id} value={String(unit.id)}>
                        {unit.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className='flex items-center gap-3'>
                <Label className='min-w-28 font-normal text-muted-foreground'>Closed between</Label>
                <DateRangePicker value={range} onChange={setRange} />
              </div>
              <div className='flex items-start gap-3'>
                <Label className='min-w-28 pt-1 font-normal text-muted-foreground'>Intents</Label>
                <div className='min-w-0 flex-1 space-y-1'>
                  {range.startDate && range.endDate ? (
                    <IntentPicker
                      intents={available}
                      selected={intents}
                      loading={intentsLoading}
                      error={intentsError}
                      onToggle={(id) =>
                        setIntents((chosen) =>
                          chosen.includes(id) ? chosen.filter((each) => each !== id) : [...chosen, id]
                        )
                      }
                    />
                  ) : (
                    <p className='pt-1 text-xs text-muted-foreground'>Choose the dates to see intents.</p>
                  )}
                  <p className='text-xs text-muted-foreground'>
                    The import reads each ticket's first customer message and keeps those with a chosen
                    intent, up to the max. None chosen imports by date alone.
                  </p>
                </div>
              </div>
              <MaxTickets value={filtersMax} onChange={setFiltersMax} />
            </Section>
          ) : (
            <Section title='Ticket IDs'>
              <label className='flex cursor-pointer items-center gap-3 rounded-md border border-dashed p-3 hover:bg-muted/50'>
                <FileSpreadsheet className='size-5 shrink-0 text-muted-foreground' />
                <span className='min-w-0 flex-1'>
                  <span className='block truncate text-sm'>{fileName ?? 'Choose a .csv or .xlsx file'}</span>
                  <span className='block text-xs text-muted-foreground'>
                    An export from the helpdesk or a sheet with an ID column
                  </span>
                </span>
                <input
                  type='file'
                  accept='.csv,.xlsx,.xls'
                  className='sr-only'
                  aria-label='Ticket ID file'
                  onChange={(event) => onFile(event.target.files?.[0])}
                />
              </label>
              {sheet ? (
                <div className='flex items-center gap-3'>
                  <Label className='min-w-28 font-normal text-muted-foreground'>ID column</Label>
                  <Select value={column ?? undefined} onValueChange={setColumn}>
                    <SelectTrigger className='min-w-56' aria-label='ID column'>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {sheet.columns.map((name) => (
                        <SelectItem key={name} value={name}>
                          {name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              ) : (
                <Textarea
                  aria-label='Paste ticket IDs'
                  placeholder={'Or paste IDs, one per line\n100234\n100235'}
                  value={pasted}
                  onChange={(event) => setPasted(event.target.value)}
                  className='min-h-24 font-mono text-xs'
                />
              )}
              <p className='text-xs text-muted-foreground'>
                {ticketIds.length.toLocaleString()} unique IDs
                {sheet ? ` from ${sheet.rows.length.toLocaleString()} rows` : ''}.
              </p>
              <p className='text-xs text-muted-foreground'>
                Use the ID the helpdesk's API knows the ticket by. Richpanel: the conversation number.
                Zoho Desk: the long ID in the ticket's URL, not its ticket number. Help Scout: the
                conversation ID in its URL, not its number. Any other ID comes back as not found.
              </p>
              <MaxTickets value={idsMaxCount} onChange={setIdsMax} />
            </Section>
          )}

          {tab !== 'full' && (
            <Section title='Check'>
              {'error' in built ? (
                <p className='text-sm text-muted-foreground'>{built.error}</p>
              ) : currentCheck ? (
                <CheckSummary check={currentCheck} />
              ) : (
                <Button variant='outline' size='sm' onClick={runCheck} disabled={busy != null}>
                  {busy === 'check' ? <Spinner className='size-3.5' /> : <Search className='size-3.5' />}
                  Check tickets
                </Button>
              )}
            </Section>
          )}
        </div>

        <SheetFooter className='shrink-0 flex-row justify-end gap-2 border-t px-5 py-3'>
          <Button variant='outline' onClick={() => onOpenChange(false)}>
            {tab === 'full' ? 'Close' : 'Cancel'}
          </Button>
          {tab !== 'full' && (
            <Button onClick={start} disabled={!canStart}>
              {busy === 'start' && <Spinner className='size-3.5' />}
              Start import
            </Button>
          )}
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
}
